import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '../../ui/Icon';
import { PushToast } from '../../ui/primitives';
import type { BoardPrintRequest } from '../../magazyn/MagazynBoard';
import {
  OrderStockStatus, OrderStockTag, STOCK_KEYS, StockConfig, StockOverview, plural, resetTimeLabel, stockErrorText, untilLabel,
} from '../../magazyn/api';

/* Magazyn w panelu admina — drobne elementy wspólne dla strony, telefonu i listy zamówień. */

export const TAG_TONE: Record<OrderStockStatus, { cls: string; icon: string }> = {
  ok: { cls: 'pill-ok', icon: 'circle-check' },
  partial: { cls: 'pill-warn', icon: 'circle-alert' },
  short: { cls: 'pill-err', icon: 'circle-x' },
};

/** „Brakuje: 2× Chleb żytni, 1× Shokupan" — do podpowiedzi przy znaczniku. */
export function missingText(tag: OrderStockTag): string {
  if (!tag.missing.length) return 'Wszystko jest na stanie';
  return 'Brakuje: ' + tag.missing.map((m) => `${m.quantity}× ${m.name}`).join(', ');
}

/** Znacznik zapasu przy zamówieniu (zielony / bursztynowy / czerwony). */
export function StockTagPill({ tag, label }: { tag: OrderStockTag; label?: string }) {
  const tone = TAG_TONE[tag.status] || TAG_TONE.short;
  return (
    <span className={`pill ${tone.cls} mga-tag`} title={missingText(tag)}>
      <Icon name={tone.icon} size={12} /> {label || tag.label}
    </span>
  );
}

/** Podgląd znacznika z dowolnym tekstem (ustawienia — podgląd na żywo). */
export function TagPreview({ status, text }: { status: OrderStockStatus; text: string }) {
  const tone = TAG_TONE[status];
  return (
    <span className={`pill ${tone.cls} mga-tag`}>
      <Icon name={tone.icon} size={12} /> {text.trim() || '…'}
    </span>
  );
}

/** Podpis resetu: „Reset stanu o 24:00 · za 13 h" albo „Bez codziennego resetu". */
export function resetInfo(src: Pick<StockOverview, 'resetEnabled' | 'resetTime' | 'nextResetAt'> | Pick<StockConfig, 'resetEnabled' | 'resetTime'> | undefined): string {
  if (!src) return '';
  if (!src.resetEnabled) return 'Bez codziennego resetu';
  const next = 'nextResetAt' in src ? src.nextResetAt : null;
  const until = next ? untilLabel(next) : '';
  return `Reset stanu o ${resetTimeLabel(src.resetTime)}${until ? ` · ${until}` : ''}`;
}

export function demandScopeText(scope: 'now' | 'all' | undefined): string {
  return scope === 'all' ? 'zapotrzebowanie: wszystkie otwarte zamówienia' : 'zapotrzebowanie: kolejka na teraz';
}

/** Wydruk z tablicy Magazynu w panelu: wynik jako toast (bez ekranu postępu jak w kiosku). */
export function useAdminStockPrint(push: PushToast) {
  return useCallback(
    async (req: BoardPrintRequest) => {
      try {
        const r = await req.run();
        if (r.jobId) push(r.message || `${req.title} — wysłano do druku`, 'ok');
        else push(r.message || 'Nie ma nic do wydrukowania', 'info');
      } catch (e) {
        push(stockErrorText(e, 'Nie udało się wydrukować'), 'err');
      }
    },
    [push],
  );
}

/** Odświeżenie wszystkiego, co zależy od ustawień Magazynu (nawigacja, tablica, znaczniki). */
export function useInvalidateStockConfig() {
  const qc = useQueryClient();
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: STOCK_KEYS.config });
    qc.invalidateQueries({ queryKey: STOCK_KEYS.overview });
    qc.invalidateQueries({ queryKey: STOCK_KEYS.items });
    qc.invalidateQueries({ queryKey: ['pack-orders'] });
  }, [qc]);
}

/** „3 nowe pozycje" / „5 nowych pozycji" / „1 nową pozycję" (biernik po „Dodano"). */
export function addedPositions(n: number): string {
  if (n === 1) return '1 nową pozycję';
  return `${n} ${plural(n, ['nową pozycję', 'nowe pozycje', 'nowych pozycji'])}`;
}
